/**
 * What a phone browser is shown instead of the guide: the two stores, and nothing else.
 *
 * Thirty-four teachers had signed in on a phone and never installed anything. Everything
 * that makes this a convention application rather than a website — a notification when a
 * room moves, recording a seminar and getting the transcript, the card on the lock screen —
 * is impossible in a browser on a phone, and Apple will not allow otherwise. So the phone
 * website stops being a lesser copy of the application and becomes the way into it.
 *
 * Both badges, always. Which store a phone belongs to is knowable and the answer is nearly
 * always right, but "nearly" puts an Android teacher in front of an App Store button on a
 * convention morning, and offering both costs one line of a screen.
 *
 * The artwork is Apple's and Google's own, served from this origin. Both publish the files
 * and both have rules about redrawing them.
 */
import { SHELL_USER_AGENT_TOKEN } from "@verity-connect/domain";

export function StoreLanding({ appName, eventName, dates }: {
  appName: string;
  eventName: string;
  dates: string | null;
}) {
  return (
    <main className="store-landing">
      <img className="store-landing-icon" src="/brand/icon-192.png" alt="" width={104} height={104} />
      <h1 data-page-heading tabIndex={-1}>{appName}</h1>
      {eventName ? (
        <p className="store-landing-event">
          {eventName}
          {dates ? <><br />{dates}</> : null}
        </p>
      ) : null}
      <div className="store-landing-badges">
        <a href="https://apps.apple.com/us/app/id6803106920">
          <img src="/brand/app-store-badge.svg" alt="Download on the App Store" width={180} height={60} />
        </a>
        <a href="https://play.google.com/store/apps/details?id=app.wvcea.connect">
          <img src="/brand/google-play-badge.png" alt="Get it on Google Play" width={180} height={70} />
        </a>
      </div>
    </main>
  );
}

/**
 * Whether this phone browser is offered the stores instead of the page it asked for.
 *
 * The Worker decides who is a phone browser and marks the document; this decides which
 * addresses are worth interrupting. Three are not, and each of them breaks the convention
 * if it is:
 *
 * `/present` is where a scanned QR code lands. A teacher checking in to a seminar without
 * the application must still be counted, or her continuing-education record silently never
 * fills. `/sign-in` and `/join` are where the links in our own emails land, and a person
 * who cannot finish signing in cannot install her way out of it either.
 *
 * `/delete-account` is the address Google was given, and Google requires it to be readable
 * by somebody with no account and no app. That is the one person this page would turn away:
 * an Android phone opening it got two store badges where the deletion page should be.
 */
export function offerTheStores(pathname: string, onAPhoneBrowser = isPhoneBrowser()): boolean {
  if (!onAPhoneBrowser) return false;
  return !KEEPS_WORKING.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

/**
 * What the Worker decided about this request, unless the page knows better.
 *
 * The Worker decides this from the User-Agent, and one kind of request cannot carry the
 * shell's name: a service worker's, inside the installed Android application, because
 * Android offers no way to set a User-Agent for those. Every page that worker fetched came
 * back marked as a phone browser, and a teacher got two store badges on every screen.
 *
 * The Worker now remembers the shell in a cookie, which fixes it at the source. This is the
 * second line, for the moments the cookie cannot cover — a cleared cookie jar whose next
 * request is one the service worker answers, or a document the worker saved before any of
 * this shipped and still holds.
 *
 * It is not the browser guessing that the comment above warns against. The page is not
 * working out what kind of device this is; it is reading the one fact the document could
 * not carry and the page has first hand. `index.html` already does exactly this for the
 * shell mark, for the same reason: a saved copy arrives without one.
 */
export function isPhoneBrowser(): boolean {
  if (typeof document === "undefined") return false;
  if (!document.documentElement.dataset.phoneBrowser) return false;
  return !insideTheApplication();
}

/**
 * Whether this page is being drawn inside the installed application.
 *
 * Read from the page's own User-Agent, which carries the shell's name even when the request
 * that fetched the document did not.
 */
export function insideTheApplication(
  userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent,
): boolean {
  return userAgent.includes(SHELL_USER_AGENT_TOKEN);
}

const KEEPS_WORKING = ["/present", "/sign-in", "/join", "/account", "/delete-account", "/mobile-entry"] as const;

